import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { AliasService } from 'src/app/authentication/alias/alias.service';
import {
  ClassificationDto,
  ClassificationIndexType,
} from 'src/app/project-setup/index-and-classification/state/classifications.store';
import { Index } from 'src/app/project-setup/index-and-classification/state/indexes.store';
import { DeliverableTypeDeprecated } from 'src/app/shared/models/deliverables/deliverables.interface';
import { DataType, Type } from '../../shared/models/datatype';
import { LabelingRule, LabelingRulesPropertyDefsList } from '../../shared/models/labeling-rule';
import { ResponsibleDiscipline } from '../../shared/models/responsible-discipline';
import { prettyName } from '../service-utils';
import { CreateProjectParams, ProjectDto, ProjectId, ProjectStore } from '../state/project.store';
import { AuthService } from './auth.service';
import { ProjectApiService } from './project-api.service';

export interface ProjectUserStatsDto {
  projectId: number;
  projectName?: string;
  assignedUserCount: number;
  assignedUsers: string[];
}

export interface ProjectMainContactDto {
  projectId: number;
  userId: number;
  userName: string;
}

export interface ProjectMainContactOperationDto {
  projectId: number;
  userId: number;
  userName: string;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class ProjectService {
  readonly projectStore = inject(ProjectStore);
  readonly #http = inject(HttpClient);
  readonly #projectApiService = inject(ProjectApiService);
  readonly #authService = inject(AuthService);
  readonly #aliasService = inject(AliasService);

  readonly #projectResource = '/api/projects/';

  /**
   * Utility accessor to ease on spec mocking due to store being cumbersome to mock.
   */
  currentProject$ = toObservable(this.projectStore.currentProject);
  projects$ = toObservable(this.projectStore.projects);

  readonly currentProjectId = this.projectStore.currentProjectId;

  constructor() {
    this.projectStore.loadBySignal(
      combineLatest([
        this.#authService.user$.pipe(
          distinctUntilChanged(),
          filter(user => !!user?.email), // Ensure user is valid, when we sign out we set the email to ''
        ),
        toObservable(this.#aliasService.alias),
      ]),
    );
  }

  setCurrentProjectById(id: number | undefined) {
    this.projectStore.setCurrentId(id);
  }

  setSelectedProject(project: ProjectDto | undefined): void {
    this.setCurrentProjectById(project?.id);
  }

  getCopyFrom(): Observable<ProjectDto[]> {
    const packageUrl = `${this.#projectResource}copyfrom`;
    return this.#http.get<ProjectDto[]>(packageUrl);
  }

  getProjectUserStats(): Observable<ProjectUserStatsDto[]> {
    return this.#http.get<ProjectUserStatsDto[]>(`${this.#projectResource}user-stats`);
  }

  getProjectMainContacts(projectId: number): Observable<ProjectMainContactDto[]> {
    return this.#http.get<ProjectMainContactDto[]>(`${this.#projectResource}${projectId}/maincontacts`);
  }

  addProjectMainContacts(
    projectId: number,
    userNames: string[],
  ): Observable<ProjectMainContactOperationDto[]> {
    return this.#http.post<ProjectMainContactOperationDto[]>(
      `${this.#projectResource}${projectId}/maincontacts`,
      userNames,
    );
  }

  removeProjectMainContacts(
    projectId: number,
    userNames: string[],
  ): Observable<ProjectMainContactOperationDto[]> {
    return this.#http.delete<ProjectMainContactOperationDto[]>(
      `${this.#projectResource}${projectId}/maincontacts`,
      {
        body: userNames,
      },
    );
  }

  createProject(newProject: CreateProjectParams): Promise<ProjectDto> {
    return this.projectStore.createProject(newProject);
  }

  editProject(project: ProjectDto) {
    return this.projectStore.updateProject(project);
  }

  updateProjectComments(projectId: number, comments: string): Observable<ProjectDto & { comments?: string }> {
    return this.#http.put<ProjectDto & { comments?: string }>(
      `${this.#projectResource}${projectId}/comments`,
      comments,
    );
  }

  deleteProject(project: ProjectDto) {
    return this.projectStore.deleteProject(project);
  }

  getResponsibleDisciplines(projectId: ProjectId): Observable<ResponsibleDiscipline[]> {
    const disciplineUrl = `${this.#projectResource}${projectId}/disciplines`;
    return this.#http.get<ResponsibleDiscipline[]>(disciplineUrl);
  }

  createDiscipline(projectId: number): Observable<any> {
    const disciplineUrl = `${this.#projectResource}${projectId}/disciplines`;
    const newDiscipline = {
      code: '',
      description: '',
      status: 'DRAFT',
    };
    return this.#http.post<any>(disciplineUrl, newDiscipline);
  }

  deleteDiscipline(projectId: number, responsibleDisciplineId: number): Observable<any> {
    const deleteDisciplineUrl = `${this.#projectResource}${projectId}/disciplines/${responsibleDisciplineId}`;
    return this.#http.delete<any>(deleteDisciplineUrl);
  }

  updateDiscipline(discipline: any, projectId: number): Observable<any> {
    const updateDisciplineUrl = `${this.#projectResource}${projectId}/disciplines/${discipline.id}`;
    const updateDisciplineObj = {
      code: discipline.code,
      description: discipline.description,
      status: discipline.status.toUpperCase(),
    };
    return this.#http.put<any>(updateDisciplineUrl, updateDisciplineObj);
  }

  getIndexes(projectId: number): Observable<Index[]> {
    return this.#http.get<Index[]>(`${this.#projectResource}${projectId}/indexes`);
  }

  createIndex(projectId: number, type: ClassificationIndexType): Observable<Index> {
    const url = `${this.#projectResource}${projectId}/indexes`;
    const newIndexDefinition = {
      name: '',
      status: 'DRAFT',
      type,
    };
    return this.#http.post<Index>(url, newIndexDefinition);
  }

  deleteIndex(projectId: number, indexId: number): Observable<Index> {
    return this.#http.delete<Index>(`${this.#projectResource}${projectId}/indexes/${indexId}`);
  }

  updateIndex(index: any, projectId: number): Observable<Index> {
    const updateDisciplineObj = {
      id: index.id,
      name: index.name,
      status: index.status.toUpperCase(),
      type: index.type,
    };
    return this.#http.put<Index>(
      `${this.#projectResource}${projectId}/indexes/${index.id}`,
      updateDisciplineObj,
    );
  }

  getDeliverablesByProject(projectId: number): Observable<DeliverableTypeDeprecated[]> {
    const getDeliverablesUrl = `${this.#projectResource}${projectId}/deliverabletypes`;
    return this.#http.get<DeliverableTypeDeprecated[]>(getDeliverablesUrl);
  }

  getProjectClassifications(projectId: number) {
    return this.#projectApiService.getClassifications({ projectId });
  }

  createClassification(projectId: number, newClassification: { [key: string]: any }): Observable<any> {
    const createClassificationUrl = `${this.#projectResource}${projectId}/classifications`;
    return this.#http.post(createClassificationUrl, newClassification);
  }

  updateClassification(projectId: number, classificationToUpdate: ClassificationDto): Observable<any> {
    const updateClassificationUrl = `${this.#projectResource}${projectId}/classifications/${classificationToUpdate.id}`;

    return this.#http.put(updateClassificationUrl, classificationToUpdate);
  }

  deleteClassification(projectId: number, classificationToDelete: { [key: string]: any }): Observable<any> {
    const deleteClassificationUrl = `${this.#projectResource}${projectId}/classifications/${classificationToDelete.id}`;
    return this.#http.delete<any>(deleteClassificationUrl);
  }

  getLabelingRules(projectId: number): Observable<LabelingRule[]> {
    const getLabelingRulesUrl = `${this.#projectResource}${projectId}/labelingrules`;
    return this.#http.get<any>(getLabelingRulesUrl);
  }

  getTagLabelingRules(projectId: number): Observable<LabelingRule[]> {
    const url = `${this.#projectResource}${projectId}/taglabelingrules`;
    return this.#http.get<any>(url);
  }

  deleteLabelingRule(projectId: number, labelingRuleId: number): Observable<any> {
    const deleteLabelingRuleUrl = `${this.#projectResource}${projectId}/labelingrules/${labelingRuleId}`;
    return this.#http.delete<any>(deleteLabelingRuleUrl);
  }

  createLabelingRule(projectId: number, labelingRule: LabelingRule): Observable<LabelingRule> {
    const rule = this.#trimNameAndDescription(labelingRule);
    const createLabelingRuleUrl = `${this.#projectResource}${projectId}/labelingrules`;
    return this.#http.post<LabelingRule>(createLabelingRuleUrl, rule);
  }

  updateLabelingRule(projectId: number, labelingRule: LabelingRule): Observable<LabelingRule> {
    const rule = this.#trimNameAndDescription(labelingRule);
    const updateLabelingRuleUrl = `${this.#projectResource}${projectId}/labelingrules/${labelingRule.id}`;
    return this.#http.put<LabelingRule>(updateLabelingRuleUrl, rule);
  }

  getLabelingRulePropertyDefinitions(projectId: number): Observable<LabelingRulesPropertyDefsList> {
    const getLabelingRulePropertyDefinitionsUrl = `${this.#projectResource}${projectId}/labelingrules/propertydefinitions`;
    return this.#http.get<LabelingRulesPropertyDefsList>(getLabelingRulePropertyDefinitionsUrl);
  }

  getDatatypes(projectId: number): Observable<Type[]> {
    const datatypesUrl = `/api/projects/${projectId}/datatypes`;
    return this.#http.get<DataType>(datatypesUrl).pipe(
      map(types => {
        types.customTypes.forEach(customType => (customType.isCustomList = true));
        types.notAssignableTypes.forEach(notAssignableType => (notAssignableType.notAssignable = true));
        const concatinatedTypes = types.standardTypes.concat(types.customTypes, types.notAssignableTypes);
        concatinatedTypes.forEach(type => {
          if (!type.isCustomList) {
            type.dataType = prettyName(type.dataType);
          }
        });
        return concatinatedTypes;
      }),
    );
  }

  #trimNameAndDescription(labelingRule: LabelingRule): LabelingRule {
    labelingRule.name = labelingRule.name ? labelingRule.name.trim() : '';
    labelingRule.description = labelingRule.description ? labelingRule.description.trim() : '';
    return labelingRule;
  }

  importGroups(projectId: number): Observable<{}> {
    const url = `${this.#projectResource}${projectId}/importgroups`;
    return this.#http.patch<{}>(url, '');
  }
}
